import type { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslations } from '@/hooks/useTranslations'

import { BillExtraChargesGrid } from '@/components/pages/Admin/BillManagement/Form/BillExtraChargesGrid'
import { BillLineItemsGrid } from '@/components/pages/Admin/BillManagement/Form/BillLineItemsGrid'
import { BillCustomerTextCombobox } from '@/components/pages/Admin/BillManagement/Form/BillCustomerTextCombobox'
import { BillWholesaleCustomerField } from '@/components/pages/Admin/BillManagement/Form/BillWholesaleCustomerField'
import {
  excelCellInputClass,
  excelCellSelectTriggerClass,
  type BillFormViewSharedProps,
} from '@/components/pages/Admin/BillManagement/Form/billFormViewShared'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  BILL_BUY_PARTY_LABELS,
  BILL_BUY_PARTY_NAME_LABELS,
  BILL_BUY_PARTY_NAME_PLACEHOLDERS,
  BILL_BUY_PARTY_VALUES,
} from '@/constants/bill-buy-party'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES } from '@/constants/bill-location'
import { formatNumber } from '@/utils/number'
import { cn } from '@/lib/utils'

const COL_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'] as const

function SheetRow({
  rowNumber,
  label,
  children,
  colSpan = 1,
}: {
  rowNumber: number
  label: ReactNode
  children: ReactNode
  colSpan?: number
}) {
  return (
    <tr>
      <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
        {rowNumber}
      </td>
      <td className="w-44 border border-border/70 bg-muted/20 px-2 py-1 text-xs font-medium text-foreground">
        {label}
      </td>
      <td className="border border-border/70 p-0 align-middle" colSpan={colSpan}>
        {children}
      </td>
      {colSpan < 5
        ? Array.from({ length: 5 - colSpan }).map((_, i) => (
            <td key={i} className="border border-border/70 bg-background/50" />
          ))
        : null}
    </tr>
  )
}

function SheetSectionHeader({ rowNumber, title }: { rowNumber: number; title: string }) {
  return (
    <tr>
      <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
        {rowNumber}
      </td>
      <td
        colSpan={6}
        className="border border-border/70 bg-primary/10 px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-foreground"
      >
        {title}
      </td>
    </tr>
  )
}

function SheetSummaryRow({
  rowNumber,
  label,
  value,
  unit,
  emphasize,
}: {
  rowNumber: number
  label: string
  value: string
  unit: string
  emphasize?: boolean
}) {
  return (
    <tr className={cn(emphasize && 'bg-primary/5')}>
      <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
        {rowNumber}
      </td>
      <td className="border border-border/70 px-2 py-1.5 text-xs font-medium">{label}</td>
      <td
        colSpan={5}
        className={cn(
          'border border-border/70 px-2 py-1.5 text-right text-sm tabular-nums',
          emphasize && 'font-bold',
        )}
      >
        {value} <span className="text-muted-foreground">{unit}</span>
      </td>
    </tr>
  )
}

export function BillFormExcelView({
  control,
  isLoading,
  isBuyBill,
  isWholesaleSell,
  billTypeWatch,
  buyPartyForLabels,
  onBillTypeChange,
  lineItemFields,
  appendLineItem,
  removeLineItem,
  lineItems,
  syncWholesaleRowWeight,
  extraChargeFields,
  appendExtraCharge,
  removeExtraCharge,
  extraCharges,
  customerNameSuggestions,
  profileLoading,
  applyCustomerProfile,
  customerNameWatch,
  totalKg,
  subtotalBaht,
  extraChargeTotal,
  discountWatch,
  isWholesaleSellSummary,
  netBaht,
  lineItemsErrorMessage,
  hasLineItemsError,
}: BillFormViewSharedProps) {
  const t = useTranslations('admin.bill_management.form')
  const { setValue, getValues } = useFormContext()

  let row = 1

  const nextRow = () => {
    const current = row
    row += 1
    return current
  }

  const billInfoHeaderRow = nextRow()
  const billDateRow = nextRow()
  const locationRow = nextRow()
  const billTypeRow = nextRow()
  const saleModeRow = billTypeWatch === 'sell' ? nextRow() : null
  const buyPartyRow = isBuyBill ? nextRow() : null
  const containerRow = isWholesaleSell ? nextRow() : null
  const custNoRow = isWholesaleSell ? nextRow() : null
  const discountRow = isWholesaleSell ? nextRow() : null
  const extraChargesHeaderRow = isWholesaleSell ? nextRow() : null
  const extraChargesBodyRow = isWholesaleSell ? nextRow() : null

  const customerHeaderRow = nextRow()
  const customerNameRow = nextRow()
  const phoneRow = nextRow()
  const carRow = nextRow()
  const addressRow = isWholesaleSell ? nextRow() : null
  const taxIdRow = isWholesaleSell ? nextRow() : null
  const brandMarkRow = isWholesaleSell ? nextRow() : null

  const orderHeaderRow = nextRow()
  const orderBodyRow = nextRow()

  const paymentHeaderRow = nextRow()
  const paymentMethodRow = nextRow()
  const paymentStatusRow = nextRow()

  const summaryHeaderRow = nextRow()
  const summaryKgRow = nextRow()
  const summarySubtotalRow = nextRow()
  const summaryExtraRow = isWholesaleSellSummary ? nextRow() : null
  const summaryDiscountRow = isWholesaleSellSummary ? nextRow() : null
  const summaryNetRow = nextRow()

  return (
    <div className="overflow-x-auto rounded-md border border-border/80 bg-[#fafafa] shadow-sm dark:bg-background">
      <table className="min-w-[56rem] w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-10 border border-border/70 bg-muted/60 px-1.5 py-1 text-center text-[10px] font-semibold text-muted-foreground" />
            {COL_LETTERS.map((letter) => (
              <th
                key={letter}
                className="border border-border/70 bg-muted/60 px-1.5 py-1 text-center text-[10px] font-semibold text-muted-foreground"
              >
                {letter}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <SheetSectionHeader rowNumber={billInfoHeaderRow} title={t('bill_information.title')} />

          <SheetRow rowNumber={billDateRow} label="วันที่บิล *">
            <FormField
              control={control}
              name="bill_date"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Input type="date" className={excelCellInputClass} {...field} />
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          <SheetRow rowNumber={locationRow} label="สถานที่ *">
            <FormField
              control={control}
              name="location"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={excelCellSelectTriggerClass}>
                        <SelectValue placeholder="เลือกจังหวัด" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BILL_LOCATION_VALUES.map((loc) => (
                        <SelectItem key={loc} value={loc}>
                          {BILL_LOCATION_LABELS[loc]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          <SheetRow rowNumber={billTypeRow} label={`${t('bill_information.bill_type.label')} *`}>
            <FormField
              control={control}
              name="bill_information.bill_type"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value)
                        if (value === 'buy') {
                          setValue('bill_information.sale_mode', 'retail')
                          if (!getValues('bill_information.buy_party_type')) {
                            setValue('bill_information.buy_party_type', 'stall')
                          }
                        } else {
                          setValue('bill_information.buy_party_type', undefined)
                        }
                        onBillTypeChange(value)
                      }}
                    >
                      <SelectTrigger className={excelCellSelectTriggerClass}>
                        <SelectValue placeholder={t('bill_information.bill_type.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buy">{t('bill_information.bill_type.buy')}</SelectItem>
                        <SelectItem value="sell">{t('bill_information.bill_type.sell')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          {saleModeRow != null ? (
            <SheetRow rowNumber={saleModeRow} label={t('bill_information.sale_mode_label')} colSpan={5}>
              <FormField
                control={control}
                name="bill_information.sale_mode"
                render={({ field }) => (
                  <FormItem className="space-y-0 px-1.5 py-1">
                    <FormControl>
                      <RadioGroup
                        className="flex flex-wrap gap-3"
                        value={field.value ?? 'retail'}
                        onValueChange={field.onChange}
                      >
                        <Label
                          htmlFor="excel_sale_mode_retail"
                          className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
                        >
                          <RadioGroupItem value="retail" id="excel_sale_mode_retail" />
                          {t('bill_information.sale_mode_retail')}
                        </Label>
                        <Label
                          htmlFor="excel_sale_mode_wholesale"
                          className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
                        >
                          <RadioGroupItem value="wholesale" id="excel_sale_mode_wholesale" />
                          {t('bill_information.sale_mode_wholesale')}
                        </Label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {buyPartyRow != null ? (
            <SheetRow
              rowNumber={buyPartyRow}
              label={`${t('bill_information.buy_party_type_label')} *`}
              colSpan={5}
            >
              <FormField
                control={control}
                name="bill_information.buy_party_type"
                render={({ field }) => (
                  <FormItem className="space-y-0 px-1.5 py-1">
                    <FormControl>
                      <RadioGroup
                        className="flex flex-wrap gap-3"
                        value={field.value ?? 'stall'}
                        onValueChange={field.onChange}
                      >
                        {BILL_BUY_PARTY_VALUES.map((party) => (
                          <Label
                            key={party}
                            htmlFor={`excel_buy_party_${party}`}
                            className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
                          >
                            <RadioGroupItem value={party} id={`excel_buy_party_${party}`} />
                            {BILL_BUY_PARTY_LABELS[party]}
                          </Label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {containerRow != null ? (
            <SheetRow rowNumber={containerRow} label={t('bill_information.wholesale_container')}>
              <FormField
                control={control}
                name="bill_information.container_no"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Input
                        className={excelCellInputClass}
                        placeholder={t('bill_information.wholesale_container_placeholder')}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage className="px-1.5 text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {custNoRow != null ? (
            <SheetRow rowNumber={custNoRow} label={t('bill_information.wholesale_cust_no')}>
              <FormField
                control={control}
                name="bill_information.customer_sequence_no"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Input
                        className={cn(excelCellInputClass, 'cursor-not-allowed bg-muted/40')}
                        placeholder={t('bill_information.wholesale_cust_no_placeholder')}
                        readOnly
                        disabled
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage className="px-1.5 text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {discountRow != null ? (
            <SheetRow rowNumber={discountRow} label={t('bill_information.wholesale_discount')}>
              <FormField
                control={control}
                name="bill_information.discount_baht"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        className={cn(excelCellInputClass, 'tabular-nums')}
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const v = e.target.value
                          field.onChange(v === '' ? 0 : parseFloat(v))
                        }}
                      />
                    </FormControl>
                    <FormMessage className="px-1.5 text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {extraChargesHeaderRow != null && extraChargesBodyRow != null ? (
            <>
              <SheetSectionHeader
                rowNumber={extraChargesHeaderRow}
                title={t('bill_information.wholesale_extra_charges')}
              />
              <tr>
                <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
                  {extraChargesBodyRow}
                </td>
                <td colSpan={6} className="border border-border/70 p-0">
                  <BillExtraChargesGrid
                    control={control}
                    fields={extraChargeFields}
                    append={appendExtraCharge}
                    remove={removeExtraCharge}
                    extraCharges={extraCharges}
                    disabled={isLoading}
                    variant="excel"
                  />
                </td>
              </tr>
            </>
          ) : null}

          <SheetSectionHeader rowNumber={customerHeaderRow} title={t('user_information.title')} />

          <SheetRow
            rowNumber={customerNameRow}
            label={
              isBuyBill
                ? `${BILL_BUY_PARTY_NAME_LABELS[buyPartyForLabels]} *`
                : `${t('user_information.username.label')} *`
            }
            colSpan={5}
          >
            <FormField
              control={control}
              name="customer_information.name"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <BillCustomerTextCombobox
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      suggestions={customerNameSuggestions}
                      loading={profileLoading}
                      placeholder={
                        isBuyBill
                          ? BILL_BUY_PARTY_NAME_PLACEHOLDERS[buyPartyForLabels]
                          : t('user_information.username.placeholder')
                      }
                      disabled={isLoading}
                      className={excelCellInputClass}
                      onSelectOption={(name) => {
                        void applyCustomerProfile(name)
                      }}
                    />
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          <SheetRow rowNumber={phoneRow} label={t('user_information.phone.label')}>
            <FormField
              control={control}
              name="customer_information.phone"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Input
                      className={excelCellInputClass}
                      placeholder={t('user_information.phone.placeholder')}
                      disabled={isLoading}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          <SheetRow rowNumber={carRow} label={t('user_information.car_registration.label')}>
            <FormField
              control={control}
              name="customer_information.car_registration"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Input
                      className={excelCellInputClass}
                      placeholder={t('user_information.car_registration.placeholder')}
                      disabled={isLoading}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          {addressRow != null ? (
            <SheetRow
              rowNumber={addressRow}
              label={t('user_information.address.label')}
              colSpan={5}
            >
              <FormField
                control={control}
                name="customer_information.address"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <BillWholesaleCustomerField
                        customerName={customerNameWatch ?? ''}
                        field="address"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder={t('user_information.address.placeholder')}
                        disabled={isLoading}
                        requiresCustomerHint={t('user_information.autocomplete_requires_customer')}
                        className={excelCellInputClass}
                      />
                    </FormControl>
                    <FormMessage className="px-1.5 text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {taxIdRow != null ? (
            <SheetRow rowNumber={taxIdRow} label={t('user_information.tax_id.label')}>
              <FormField
                control={control}
                name="customer_information.tax_id"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <BillWholesaleCustomerField
                        customerName={customerNameWatch ?? ''}
                        field="tax_id"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder={t('user_information.tax_id.placeholder')}
                        disabled={isLoading}
                        requiresCustomerHint={t('user_information.autocomplete_requires_customer')}
                        className={excelCellInputClass}
                      />
                    </FormControl>
                    <FormMessage className="px-1.5 text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          {brandMarkRow != null ? (
            <SheetRow rowNumber={brandMarkRow} label={t('user_information.brand_mark.label')}>
              <FormField
                control={control}
                name="customer_information.brand_mark"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <BillWholesaleCustomerField
                        customerName={customerNameWatch ?? ''}
                        field="brand_mark"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder={t('user_information.brand_mark.placeholder')}
                        disabled={isLoading}
                        requiresCustomerHint={t('user_information.autocomplete_requires_customer')}
                        className={excelCellInputClass}
                      />
                    </FormControl>
                    <FormMessage className="px-1.5 text-xs" />
                  </FormItem>
                )}
              />
            </SheetRow>
          ) : null}

          <SheetSectionHeader rowNumber={orderHeaderRow} title={t('order_information.title')} />
          <tr>
            <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
              {orderBodyRow}
            </td>
            <td
              colSpan={6}
              className={cn(
                'border border-border/70 p-0',
                hasLineItemsError && 'ring-2 ring-inset ring-destructive/40',
              )}
            >
              <BillLineItemsGrid
                control={control}
                fields={lineItemFields}
                append={appendLineItem}
                remove={removeLineItem}
                lineItems={lineItems}
                isWholesaleSell={isWholesaleSell}
                syncWholesaleRowWeight={syncWholesaleRowWeight}
                disabled={isLoading}
                variant="excel"
              />
              {lineItemsErrorMessage ? (
                <p className="border-t border-destructive/30 px-2 py-1.5 text-xs font-medium text-destructive">
                  {lineItemsErrorMessage}
                </p>
              ) : null}
            </td>
          </tr>

          <SheetSectionHeader rowNumber={paymentHeaderRow} title={t('payment_information.title')} />

          <SheetRow
            rowNumber={paymentMethodRow}
            label={`${t('payment_information.method.label')} *`}
          >
            <FormField
              control={control}
              name="payment_information.payment_method"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className={excelCellSelectTriggerClass}>
                        <SelectValue placeholder={t('payment_information.method.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">{t('payment_information.method.cash')}</SelectItem>
                        <SelectItem value="transfer">
                          {t('payment_information.method.transfer')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          <SheetRow
            rowNumber={paymentStatusRow}
            label={`${t('payment_information.status.label')} *`}
          >
            <FormField
              control={control}
              name="payment_information.payment_status"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className={excelCellSelectTriggerClass}>
                        <SelectValue placeholder={t('payment_information.status.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">
                          {t('payment_information.status.pending')}
                        </SelectItem>
                        <SelectItem value="paid">{t('payment_information.status.paid')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="px-1.5 text-xs" />
                </FormItem>
              )}
            />
          </SheetRow>

          <SheetSectionHeader rowNumber={summaryHeaderRow} title="สรุปทั้งบิล" />
          <SheetSummaryRow
            rowNumber={summaryKgRow}
            label="น้ำหนักรวม"
            value={formatNumber(totalKg)}
            unit="กก."
          />
          <SheetSummaryRow
            rowNumber={summarySubtotalRow}
            label="ยอดทุเรียน"
            value={formatNumber(subtotalBaht)}
            unit="บาท"
          />
          {summaryExtraRow != null ? (
            <SheetSummaryRow
              rowNumber={summaryExtraRow}
              label="ค่าใช้จ่ายเพิ่ม"
              value={formatNumber(extraChargeTotal)}
              unit="บาท"
            />
          ) : null}
          {summaryDiscountRow != null ? (
            <SheetSummaryRow
              rowNumber={summaryDiscountRow}
              label="หักส่วนลด"
              value={formatNumber(discountWatch)}
              unit="บาท"
            />
          ) : null}
          <SheetSummaryRow
            rowNumber={summaryNetRow}
            label="ยอดสุทธิ (รวมภาษี 0%)"
            value={formatNumber(netBaht)}
            unit="บาท"
            emphasize
          />
        </tbody>
      </table>
      <p className="border-t border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        โหมด Excel · Tab เลื่อนเซลล์ · Enter ลงแถวถัดไปในตารางรายการ · สลับโหมดได้โดยไม่เสียข้อมูล
      </p>
    </div>
  )
}
